/**
 * Role Permissions Utility
 * Frontend role hierarchy configuration for user management
 */

// ============================================================================
// ROLE EQUIVALENCE SYSTEM
// Roles that should be treated as 'director' for permission checks
// ============================================================================
export const DIRECTOR_EQUIVALENT_ROLES = ['system_admin', 'admin', 'director'];

// Roles that should be hidden from the Users tab UI
export const HIDDEN_UI_ROLES = ['system_admin'];

/**
 * Get the effective role for permission checks
 * system_admin and admin are treated as 'director' for all permissions
 * @param {string} role - The actual user role
 * @returns {string} - The effective role for permission checks
 */
export function getEffectiveRole(role) {
  if (DIRECTOR_EQUIVALENT_ROLES.includes(role)) {
    return 'director';
  }
  return role;
}

/**
 * Check if a role has director-level access
 * @param {string} role - The role to check
 * @returns {boolean}
 */
export function hasDirectorAccess(role) {
  return DIRECTOR_EQUIVALENT_ROLES.includes(role);
}

// Define which roles each user role can manage
export const ROLE_HIERARCHY = {
  director: {
    canManage: [
      'system_admin', 'admin', 'director', 'manager', 'leader', 'agent', 'telemarketer',
      'sid', 'asid', 'designer', 'draughter_2d', 'draughter_site',
      'quotation_team', 'legal_team',
      'operation_head', 'site_manager', 'site_supervisor'
    ],
    requiresCascading: {
      agent: ['managerId', 'leaderId'],
      telemarketer: ['managerId', 'leaderId'],
      leader: ['managerId'],
      site_supervisor: ['siteManagerId'],
      asid: ['sidId'],
      designer: ['sidId'],
      draughter_2d: ['sidId']
    }
  },
  manager: {
    canManage: ['leader', 'agent', 'telemarketer'],
    requiresCascading: {
      agent: ['leaderId'],
      telemarketer: ['leaderId']
    },
    autoAssign: {
      leader: 'managerId'
    }
  },
  leader: {
    canManage: ['agent', 'telemarketer'],
    autoAssign: {
      agent: 'leaderId',
      telemarketer: 'leaderId'
    }
  },
  sid: {
    canManage: ['asid', 'designer', 'draughter_2d'],
    autoAssign: {
      asid: 'sidId',
      designer: 'sidId',
      draughter_2d: 'sidId'
    }
  },
  operation_head: {
    canManage: ['site_manager', 'site_supervisor'],
    requiresCascading: {
      site_supervisor: ['siteManagerId']
    }
  },
  site_manager: {
    canManage: ['site_supervisor'],
    autoAssign: {
      site_supervisor: 'siteManagerId'
    }
  }
};

// Role display names for UI
export const ROLE_DISPLAY_NAMES = {
  system_admin: 'System Admin',
  admin: 'Admin',
  director: 'Director',
  manager: 'Manager',
  leader: 'Team Leader',
  agent: 'Sales Agent',
  telemarketer: 'Telemarketer',
  sid: 'SID (Senior Interior Designer)',
  asid: 'ASID (Assistant SID)',
  designer: 'Designer (3D)',
  draughter_2d: '2D Draughter',
  draughter_site: 'Site Draughter',
  quotation_team: 'Quotation Team',
  legal_team: 'Legal Team',
  operation_head: 'Operation Head',
  site_manager: 'Site Manager',
  site_supervisor: 'Site Supervisor'
};

// Roles that can access the Users tab (includes director-equivalent roles)
export const USER_MANAGEMENT_ROLES = ['system_admin', 'admin', 'director', 'manager', 'leader', 'sid', 'operation_head', 'site_manager'];

/**
 * Get roles that current user can create/manage
 * Uses effective role for permission checks (system_admin/admin → director)
 * @param {string} currentUserRole - The role of the current user
 * @returns {string[]}
 */
export function getManageableRoles(currentUserRole) {
  const effectiveRole = getEffectiveRole(currentUserRole);
  return ROLE_HIERARCHY[effectiveRole]?.canManage || [];
}

/**
 * Get required cascading fields for creating a specific role
 * Uses effective role for permission checks (system_admin/admin → director)
 * @param {string} currentUserRole - The role of the current user
 * @param {string} targetRole - The role being created
 * @returns {string[]}
 */
export function getCascadingRequirements(currentUserRole, targetRole) {
  const effectiveRole = getEffectiveRole(currentUserRole);
  return ROLE_HIERARCHY[effectiveRole]?.requiresCascading?.[targetRole] || [];
}

/**
 * Get auto-assign field for a role
 * Uses effective role for permission checks (system_admin/admin → director)
 * @param {string} currentUserRole - The role of the current user
 * @param {string} targetRole - The role being created
 * @returns {string|null}
 */
export function getAutoAssignField(currentUserRole, targetRole) {
  const effectiveRole = getEffectiveRole(currentUserRole);
  return ROLE_HIERARCHY[effectiveRole]?.autoAssign?.[targetRole] || null;
}

/**
 * Check if user has Users tab access
 * @param {string} role - User role
 * @returns {boolean}
 */
export function hasUsersTabAccess(role) {
  return USER_MANAGEMENT_ROLES.includes(role);
}

/**
 * Get role display name
 * @param {string} role - Role key
 * @returns {string}
 */
export function getRoleDisplayName(role) {
  return ROLE_DISPLAY_NAMES[role] || role;
}

/**
 * Check if a role can manage another role
 * Uses effective role for permission checks (system_admin/admin → director)
 * @param {string} currentRole - The role of the current user
 * @param {string} targetRole - The role to check
 * @returns {boolean}
 */
export function canManageRole(currentRole, targetRole) {
  const effectiveRole = getEffectiveRole(currentRole);
  return ROLE_HIERARCHY[effectiveRole]?.canManage?.includes(targetRole) || false;
}
